import clsx from "clsx";
import type { EngineChip as EngineChipData, MeasurementPath } from "../types";

/**
 * §3.2 — color is meaning-bearing and reserved. Every pair is a portal
 * design-system class pair (verified in tailwind.config.js); note purple's
 * classes are `bg-purple-100 text-purple-700` — `bg-purple-light` is not a
 * class and silently renders nothing.
 */
export type ChipTone =
  | "green" // verified-good
  | "blue" // proving / commercial
  | "red" // broken
  | "orange" // waiting / caution
  | "purple" // research / engine-scope / GA4
  | "teal" // our fix in flight
  | "neutral"; // facts & gates

const TONE_CLASSES: Record<ChipTone, string> = {
  green: "bg-success-light text-success-dark",
  blue: "bg-info-light text-info-dark",
  red: "bg-danger-light text-danger-dark",
  orange: "bg-warning-light text-warning-dark",
  purple: "bg-purple-100 text-purple-700",
  teal: "bg-[#E7F1F3] text-brand-primary",
  neutral: "bg-[#F4F4F6] text-gray-600",
};

/** Small squarish tag: 4px radius, 12px medium (§3.2 geometry, copied from
 *  the onboarding StatusBadge shape). */
export function Chip({
  tone,
  children,
  className = "",
  title,
}: {
  tone: ChipTone;
  children: React.ReactNode;
  className?: string;
  /** Hover hint — drawn by `TooltipLayer`, not by the browser. */
  title?: string;
}) {
  return (
    <span
      data-tip={title}
      className={clsx(
        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap",
        TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const OUTLINED_TONE_CLASSES: Record<ChipTone, string> = {
  green: "border-success-dark text-success-dark",
  blue: "border-info-dark text-info-dark",
  red: "border-danger-dark text-danger-dark",
  orange: "border-warning-dark text-warning-dark",
  purple: "border-purple-700 text-purple-700",
  teal: "border-brand-primary text-brand-primary",
  neutral: "border-gray-400 text-gray-600",
};

/** Outlined is reserved for `confirmed`/`inferred` and `not run` (§6 rule 8).
 *  `undiagnosed` was the fourth until 2026-08-15, when that tag was retired
 *  from operator surfaces — see IncidentStatusChip, itself narrowed to
 *  outcomes only on 2026-08-17. */
export function OutlinedTag({
  tone,
  children,
  className = "",
  title,
}: {
  tone: ChipTone;
  children: React.ReactNode;
  className?: string;
  /** Hover hint — drawn by `TooltipLayer`, not by the browser. */
  title?: string;
}) {
  return (
    <span
      data-tip={title}
      className={clsx(
        "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-white border",
        OUTLINED_TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Engine chips are neutral facts; a GA4-only hotel adds a small purple
 *  `GA4` tag (§5.1 ENGINE column). */
export function EngineChips({
  engines,
  trackingMode,
}: {
  engines: EngineChipData[];
  trackingMode?: MeasurementPath;
}) {
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {engines.length === 0 && <span className="text-gray-400 text-xs">—</span>}
      {engines.map((engine) => (
        <Chip key={engine.key} tone="neutral" title={`credited by ${engine.source}`}>
          {engine.displayName}
        </Chip>
      ))}
      {trackingMode === "ga4-only" && <Chip tone="purple">GA4</Chip>}
    </span>
  );
}
